<template>
  <el-collapse-item :name="activityData.activityCVId">
    <template #title>
      <div class="activity-preview">
        <div class="activity-preview__content">
          <div class="activity-preview__heading">
            <activity-icon class="activity-preview__icon" :size="32" :activity="activityData.activity.activityId" />
            <span class="activity-preview__title">
              {{ activityData.activity.name }}
            </span>
          </div>
          <p class="activity-preview__story">
            {{ activityData.story }}
          </p>
          <div class="activity-preview__info">
            <span class="activity-preview__info-label">Level:</span>
            <span class="activity-preview__level">
              {{ activityData.level.description }}
            </span>
          </div>

          <div class="activity-preview__link">
            <el-link type="primary">
              {{ isActive ? 'Hide' : 'Read more' || 'Read more' }}
            </el-link>
          </div>
        </div>

        <div class="activity-preview__button" v-if="isCurrentUser && !isPublicCV">
          <el-button type="primary" plain @click="startEditMode">
            Edit
          </el-button>
        </div>
      </div>
    </template>

    <div :class="['activity', { 'activity--active': isActive }]" v-if="fullActivityData">
      <div class="activity__achievement" v-if="fullActivityData.achievements.length > 0">
        <div class="activity__subtitle">
          Achievements
        </div>

        <el-timeline class="activity__achievement-list">
          <el-timeline-item class="achievement" v-for="achievement in fullActivityData.achievements"
            :key="achievement.achievementId">
            <div class="achievement__dates">
              {{ formatDate(achievement.startDate, SHORT_DATE_FORMAT) }}
              <span class="achievement__dates--end" v-if="achievement.endDate">
                — {{ formatDate(achievement.endDate, SHORT_DATE_FORMAT) }}
              </span>
            </div>
            <div class="achievement__title">
              {{ achievement.achievementType.name }}
            </div>

            <div class="achievement__about">
              <span class="achievement__label">role:</span>
              <span class="achievement__value">
                {{ achievement.achievementRoleType.name }}
              </span>

              <span class="achievement__label">difficulty:</span>
              <span class="achievement__value">
                {{ achievement.achievementLevelType.name }}
              </span>

              <div class="achievement__location" v-if="achievement.place">
                <el-icon>
                  <location />
                </el-icon>
                {{ achievement.place }}
              </div>

              <div class="achievement__story" v-if="achievement.story">
                {{ achievement.story }}
              </div>
            </div>
          </el-timeline-item>
        </el-timeline>
      </div>

      <div class="activity__certificates" v-if="fullActivityData.certificates.length > 0">
        <div class="activity__subtitle">
          Certificates
        </div>

        <div class="certificate" v-for="certificate in fullActivityData.certificates" :key="certificate.certificateId">
          <div class="certificate__title">
            {{ certificate.certificateType.name }}
          </div>
          <div class="certificate__dates">
            {{ formatDate(certificate.issueDate, SHORT_DATE_FORMAT) }}
            <span class="certificate__dates-end" v-if="certificate.expirationDate">
              — {{ formatDate(certificate.expirationDate, SHORT_DATE_FORMAT) }}
            </span>
          </div>
          <div class="certificate__description">
            {{ certificate.description }}
          </div>
        </div>
      </div>

      <div class="activity__skills" v-if="fullActivityData.skills.length > 0">
        <div class="activity__subtitle">
          Skills
        </div>

        <div class="skill" v-for="skill in fullActivityData.skills" :key="skill.skillId">
          <span class="skill__title">
            {{ skill.otherSkillType || skill.skillType.name }}:
          </span>
          <span class="skill__level">
            {{ skill.level }}
          </span>
        </div>
      </div>
    </div>
  </el-collapse-item>
</template>

<script>
import {
  getCurrentUserFullActivityCV,
  getFullActivityCVByUserId,
} from '@/api/activity.js';
import { formatDate, SHORT_DATE_FORMAT } from '@/utils/index.js';
import ActivityIcon from '@/components/common/ActivityIcon.vue';

export default {
  name: 'Activity',
  props: {
    activityData: {
      type: Object,
      required: true,
    },
    isActive: {
      type: Boolean,
      required: false,
    },
    isCurrentUser: {
      type: Boolean,
      required: false,
    },
    isPublicCV: {
      type: Boolean,
      required: false,
    },
    userId: {
      type: String,
      required: true,
    },
  },
  components: {
    ActivityIcon,
  },
  data() {
    return {
      fullActivityData: null,
      SHORT_DATE_FORMAT,
    };
  },
  methods: {
    formatDate,
    async fetchFullActivityData() {
      const { activityCVId } = this.activityData;
      const { isCurrentUser, userId } = this;

      const { data, success } = isCurrentUser
        ? await getCurrentUserFullActivityCV({
          activityCVId,
        })
        : await getFullActivityCVByUserId({ activityCVId, userId });

      if (success) {
        this.fullActivityData = data;
      }
    },
    startEditMode() {
      const { activityData } = this;

      this.$router.push({
        name: 'UserProfile',
        params: {
          tab: 'cv',
        },
        query: {
          edit: true,
          activity: activityData.activityCVId,
        },
      });
    },
    handleResize() {
      //this.isMobile = window.innerWidth <= DESKTOP_SIZE;
    },
  },
  created() {
    const { isActive, fullActivityData } = this;
    if (isActive && !fullActivityData) {
      this.fetchFullActivityData();
    }
  },
  mounted() {
    const resizeObserver = new ResizeObserver(() => {
      this.$nextTick(() => {
        this.handleResize();
      });
    });
    resizeObserver.observe(this.$el);
  },
  watch: {
    isActive(newVal) {
      const { fullActivityData } = this;
      if (newVal && !fullActivityData) {
        this.fetchFullActivityData();
      }
    },
  },
};
</script>

<style lang="scss">
@import '~@/styles/shared/_globals.scss';

.activity-preview {
  display: flex;
  flex-flow: row wrap;
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 30px 25px 25px;
  box-sizing: border-box;

  &__content {
    flex: 1;
    padding-right: var(--global-dimension-size-250);
  }

  &__heading {
    display: flex;
    align-items: center;
    margin-bottom: var(--global-dimension-size-150);
  }

  &__icon {
    margin-right: var(--global-dimension-size-125);
  }

  &__title {
    font-weight: var(--global-font-weight-medium);
    font-size: var(--font-size-500);
    line-height: 1.3;
    margin-bottom: var(--global-dimension-size-150);
  }

  &__story {
    margin: 0;
    margin-bottom: var(--global-dimension-size-125);
    font-weight: normal;
    font-size: var(--font-size-200);
    line-height: 1.6;
    text-align: justify;
  }

  &__info {
    display: flex;
    align-items: center;
    margin-bottom: var(--global-dimension-size-150);
    font-weight: normal;
    font-size: var(--font-size-200);
    line-height: 1.6;
  }

  &__info-label {
    margin-right: var(--global-dimension-size-75);
    font-weight: 500;
  }

  &__link {
    margin-top: var(--global-dimension-size-200);
    text-align: left;
  }

  &__button {
    margin-top: var(--global-dimension-size-150);
    text-align: right;
  }
}

.activity {
  padding: 0 var(--global-dimension-size-300);

  &--active {}

  &__achievement,
  &__certificates,
  &__skills {
    padding-top: var(--global-dimension-size-125);
  }

  &__achievement-list {
    padding-left: 0;
  }

  &__subtitle {
    margin-bottom: var(--global-dimension-size-250);
    font-weight: normal;
    font-size: var(--global-dimension-size-225);
    line-height: 1.6;
  }

  &__empty-text {
    font-size: var(--font-size-300);
  }
}

.achievement {
  &__dates {
    margin-bottom: var(--global-dimension-size-125);
    font-size: var(--font-size-150);
    line-height: 1.6;
    color: var(--color-gray-800);

    &--end {}
  }

  &__title {
    display: inline-block;
    margin-bottom: var(--global-dimension-size-75);
    margin-right: var(--global-dimension-size-200);

    font-weight: 500;
    font-size: var(--font-size-300);
    line-height: 1.6;
  }

  &__about {
    font-size: var(--font-size-150);
    line-height: 1.6;
  }

  &__label {
    color: var(--color-gray-800);

    *+& {
      margin-left: var(--global-dimension-size-250);
    }
  }

  &__value {
    color: var(--color-gray-800);
  }

  &__location {
    margin-top: var(--global-dimension-size-125);
    font-size: var(--font-size-150);
    line-height: 1.6;
  }

  &__story {
    margin-top: var(--global-dimension-size-125);
    font-size: var(--font-size-150);
  }
}

.certificate {
  position: relative;
  margin-bottom: var(--global-dimension-size-250);
  padding-left: 25px;

  &:before {
    position: absolute;
    width: var(--global-dimension-size-125);
    height: var(--global-dimension-size-125);
    content: '';
    top: 0.5em;
    left: 0;
    background-color: var(--color-primary-400);
    border-radius: 100%;
  }

  &__title {
    margin-bottom: var(--global-dimension-size-75);
    font-weight: 500;
    font-size: var(--font-size-300);
    line-height: 1.3;
  }

  &__dates {
    font-size: var(--font-size-150);
    line-height: 1.6;
    color: var(--color-gray-800);
  }

  &__dates-end {}

  &__description {
    margin-top: var(--global-dimension-size-125);
    font-size: var(--font-size-150);
    line-height: 1.6;
  }
}

.skill {
  margin-bottom: var(--global-dimension-size-250);
  font-size: var(--global-dimension-size-200);
  line-height: 1.6;

  &__title {
    font-weight: 500;
  }

  &__level {}
}
</style>
